"use client"

import { useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Button } from "@/shared/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog"
import { useSession } from "@/features/auth/context/session.context"
import {
  getWorkScopeCompleteOptions,
  PHOTO_QUERY_KEYS,
  Photo,
  tagWorkScopeComplete,
  untagWorkScopeComplete,
  WorkScopeCompleteOption,
  WorkScopePhotoSlot,
} from "@/features/photos/api/photos.api"
import {
  canSeeWorkScopeCompleteTagControl,
  canUntagWorkScopeComplete,
  isPhotoEligibleForWorkScopeCompleteTag,
  WORK_SCOPE_COMPLETE_ALREADY_TAGGED_MESSAGE,
  workScopePhotoReplaceMessage,
} from "@/features/homeowners/lib/work-scope-complete"

type WorkScopeCompleteTagControlProps = {
  photo: Photo
  homeId: number
}

export const TAG_WORK_SCOPE_COMPLETE_LABEL = "Tag WS Photo"
export const TAG_WORK_SCOPE_COMPLETE_DIALOG_TITLE = "Tag as work scope photo"

function slotWord(slot: WorkScopePhotoSlot): string {
  return slot === "before" ? "Before" : "After"
}

export function workScopeCompleteButtonLabel(photo: Pick<Photo, "workScopeComplete">): string {
  if (!photo.workScopeComplete) return TAG_WORK_SCOPE_COMPLETE_LABEL
  return `${photo.workScopeComplete.workScopeName} ${slotWord(photo.workScopeComplete.slot)}`
}

function defaultSlotForScope(
  option: WorkScopeCompleteOption | undefined,
  currentTag: Photo["workScopeComplete"],
): WorkScopePhotoSlot {
  if (currentTag && option && option.workScopeId === currentTag.workScopeId) {
    return currentTag.slot
  }
  if (option?.beforePhotoId == null) return "before"
  return "after"
}

function invalidatePhotoTagQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  homeId: number,
) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: PHOTO_QUERY_KEYS.byHome(homeId) }),
    queryClient.invalidateQueries({ queryKey: PHOTO_QUERY_KEYS.workScopeComplete(homeId) }),
    queryClient.invalidateQueries({ queryKey: PHOTO_QUERY_KEYS.workScopeOptions(homeId) }),
  ])
}

/**
 * One button in the photo viewer. Untagged: "Tag WS Photo".
 * Tagged: "<Work scope> Before" or "<Work scope> After".
 */
export default function WorkScopeCompleteTagControl({
  photo,
  homeId,
}: WorkScopeCompleteTagControlProps) {
  const { session } = useSession()
  const queryClient = useQueryClient()
  const [isOpen, setIsOpen] = useState(false)
  const [workScopeId, setWorkScopeId] = useState("")
  const [slot, setSlot] = useState<WorkScopePhotoSlot>("before")
  const [message, setMessage] = useState<string | null>(null)
  const [pendingOption, setPendingOption] = useState<WorkScopeCompleteOption | null>(null)
  const [pendingSlot, setPendingSlot] = useState<WorkScopePhotoSlot>("before")
  const [isSaving, setIsSaving] = useState(false)

  const currentTag = photo.workScopeComplete ?? null
  const hasTagRole = canSeeWorkScopeCompleteTagControl(session?.role)
  const isEligible = isPhotoEligibleForWorkScopeCompleteTag(photo)
  const canOpen = hasTagRole && (isEligible || currentTag != null)
  const canUntag = currentTag != null && canUntagWorkScopeComplete({
    role: session?.role,
    userId: session?.sub,
    taggedByUserId: currentTag.taggedByUserId,
  })

  const optionsQuery = useQuery({
    queryKey: PHOTO_QUERY_KEYS.workScopeOptions(homeId),
    queryFn: async () => {
      const response = await getWorkScopeCompleteOptions(homeId)
      if (!response.success) {
        throw new Error(response.message || "Failed to load work scopes")
      }
      return response.data ?? []
    },
    enabled: isOpen && canOpen && Number.isFinite(homeId),
  })

  if (!canOpen && currentTag == null) return null

  const label = workScopeCompleteButtonLabel(photo)

  if (!canOpen) {
    return (
      <span
        className="inline-flex h-8 shrink-0 items-center rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground"
        aria-label={label}
      >
        {label}
      </span>
    )
  }

  const openDialog = () => {
    setWorkScopeId(currentTag ? String(currentTag.workScopeId) : "")
    setSlot(currentTag?.slot ?? "before")
    setMessage(null)
    setPendingOption(null)
    setIsOpen(true)
  }

  const closeDialog = () => {
    if (isSaving) return
    setIsOpen(false)
    setPendingOption(null)
    setMessage(null)
  }

  const selectScope = (nextId: string) => {
    setWorkScopeId(nextId)
    const option = (optionsQuery.data ?? []).find((item) => String(item.workScopeId) === nextId)
    setSlot(defaultSlotForScope(option, currentTag))
  }

  const applyTag = async (option: WorkScopeCompleteOption, nextSlot: WorkScopePhotoSlot) => {
    setIsSaving(true)
    setMessage(null)
    try {
      const leavesCurrentSlot = currentTag != null && (
        currentTag.workScopeId !== option.workScopeId || currentTag.slot !== nextSlot
      )
      if (leavesCurrentSlot) {
        if (!canUntag) {
          setMessage(WORK_SCOPE_COMPLETE_ALREADY_TAGGED_MESSAGE)
          return
        }
        const untagged = await untagWorkScopeComplete(photo.id)
        if (!untagged.success) {
          setMessage(untagged.message || "Failed to remove the current tag")
          return
        }
      }
      const response = await tagWorkScopeComplete(photo.id, option.workScopeId, nextSlot)
      if (!response.success) {
        setMessage(
          response.status === 409
            ? WORK_SCOPE_COMPLETE_ALREADY_TAGGED_MESSAGE
            : (response.message || "Failed to tag photo"),
        )
        return
      }
      await invalidatePhotoTagQueries(queryClient, homeId)
      setIsOpen(false)
    } finally {
      setIsSaving(false)
      setPendingOption(null)
    }
  }

  const handleTag = () => {
    const option = (optionsQuery.data ?? []).find((item) => String(item.workScopeId) === workScopeId)
    if (!option) return
    const existingPhotoId = slot === "before" ? option.beforePhotoId : option.completePhotoId
    if (existingPhotoId != null && existingPhotoId !== photo.id) {
      setPendingSlot(slot)
      setPendingOption(option)
      return
    }
    void applyTag(option, slot)
  }

  const handleUntag = async () => {
    setIsSaving(true)
    setMessage(null)
    try {
      const response = await untagWorkScopeComplete(photo.id)
      if (!response.success) {
        setMessage(response.message || "Failed to remove tag")
        return
      }
      await invalidatePhotoTagQueries(queryClient, homeId)
      setIsOpen(false)
    } finally {
      setIsSaving(false)
    }
  }

  const moveSlot = (next: WorkScopePhotoSlot) => {
    if (!isSaving) setSlot(next)
  }

  const onToggleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return
    event.preventDefault()
    moveSlot(event.key === "ArrowLeft" ? "before" : "after")
  }

  const isCurrentSelection = currentTag != null
    && String(currentTag.workScopeId) === workScopeId
    && currentTag.slot === slot
  const canSubmitTag = isEligible && workScopeId !== "" && !isCurrentSelection && !isSaving
  const scopeName = currentTag?.workScopeName ?? "this work scope"
  const description = pendingOption
    ? workScopePhotoReplaceMessage(pendingSlot)
    : isCurrentSelection
      ? `This photo is the ${slot} picture for ${scopeName}.`
      : "Choose whether this photo is the before or after picture, and which work scope it belongs to."

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant={currentTag ? "default" : "outline"}
        className="shrink-0"
        onClick={openDialog}
        aria-label={label}
      >
        {label}
      </Button>
      <Dialog open={isOpen} onOpenChange={(open) => { if (!open) closeDialog() }}>
        <DialogContent className="z-[110] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{TAG_WORK_SCOPE_COMPLETE_DIALOG_TITLE}</DialogTitle>
            {!pendingOption && (
              <div
                role="radiogroup"
                aria-label="Before or after"
                className="mt-2 flex rounded-full bg-neutral-200 p-1"
                onKeyDown={onToggleKeyDown}
              >
                {(["before", "after"] as const).map((value) => {
                  const selected = slot === value
                  return (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      disabled={isSaving || !isEligible}
                      onClick={() => moveSlot(value)}
                      className={
                        selected
                          ? "flex-1 rounded-full bg-[#FFC832] px-4 py-2 text-sm font-semibold text-neutral-900"
                          : "flex-1 rounded-full bg-transparent px-4 py-2 text-sm font-medium text-neutral-800"
                      }
                    >
                      {slotWord(value)}
                    </button>
                  )
                })}
              </div>
            )}
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          {pendingOption ? (
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPendingOption(null)} disabled={isSaving}>
                Cancel
              </Button>
              <Button
                type="button"
                showLoader={isSaving}
                onClick={() => { if (pendingOption) void applyTag(pendingOption, pendingSlot) }}
                disabled={isSaving}
              >
                Replace
              </Button>
            </DialogFooter>
          ) : (
            <>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-xs text-muted-foreground">Work scope</span>
                <select
                  aria-label="Work scope"
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
                  value={workScopeId}
                  onChange={(event) => selectScope(event.target.value)}
                  disabled={isSaving || optionsQuery.isLoading || !isEligible}
                >
                  <option value="">Select a work scope</option>
                  {(optionsQuery.data ?? []).map((option) => (
                    <option key={option.workScopeId} value={option.workScopeId}>
                      {option.workScopeName}
                    </option>
                  ))}
                </select>
              </label>
              {optionsQuery.isError && (
                <p role="alert" className="text-xs text-destructive">
                  {optionsQuery.error instanceof Error ? optionsQuery.error.message : "Failed to load work scopes"}
                </p>
              )}
              {message && (
                <p role="alert" className="text-xs text-destructive">
                  {message}
                </p>
              )}
              <DialogFooter className="gap-2 sm:gap-0">
                {canUntag && (
                  <Button
                    type="button"
                    variant="destructive"
                    showLoader={isSaving}
                    onClick={handleUntag}
                    disabled={isSaving}
                    className="sm:mr-auto"
                  >
                    Remove tag
                  </Button>
                )}
                <Button type="button" variant="outline" onClick={closeDialog} disabled={isSaving}>
                  Cancel
                </Button>
                <Button type="button" showLoader={isSaving} onClick={handleTag} disabled={!canSubmitTag}>
                  Tag
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
